// src/pages/PortfolioAnalysis.jsx
import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../utils/api';

const REC_STYLE = {
  'ADD MORE': { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
  HOLD:       { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  TRIM:       { bg: '#ffedd5', color: '#9a3412', border: '#fdba74' },
  EXIT:       { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
};

function RecBadge({ rec }) {
  const s = REC_STYLE[rec] || { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 6, padding: '0.2rem 0.55rem',
      fontSize: '0.74rem', fontWeight: 700, letterSpacing: '0.03em', whiteSpace: 'nowrap',
    }}>
      {rec || '—'}
    </span>
  );
}

function ScoreBar({ label, score, max, color }) {
  const pct = max > 0 ? Math.round((score / max) * 100) : 0;
  return (
    <div style={{ marginBottom: '0.35rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#64748b', marginBottom: 2 }}>
        <span>{label}</span>
        <span style={{ fontWeight: 700, color: '#0f172a' }}>{score} / {max}</span>
      </div>
      <div style={{ background: '#e2e8f0', borderRadius: 4, height: 6, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, background: color, height: '100%', borderRadius: 4, transition: 'width 0.4s' }} />
      </div>
    </div>
  );
}

function CheckIcon({ pass }) {
  if (pass === null || pass === undefined) return <span style={{ color: '#94a3b8' }}>—</span>;
  return pass
    ? <span style={{ color: '#16a34a', fontWeight: 700 }}>✓</span>
    : <span style={{ color: '#dc2626', fontWeight: 700 }}>✗</span>;
}

function TechBreakdown({ bd = {}, mom = {}, indicators = {} }) {
  const rows = [
    { label: 'Price > EMA 50 (+2)',           key: 'price_gt_ema50',    extra: indicators.ema50  ? `EMA50 ₹${indicators.ema50}`  : '' },
    { label: 'Price > EMA 200 (+1)',          key: 'price_gt_ema200',   extra: indicators.ema200 ? `EMA200 ₹${indicators.ema200}`: 'Insufficient data' },
    { label: 'EMA50 > EMA200 — golden cross', key: 'ema50_gt_ema200',   extra: '' },
    { label: 'RSI 55–78 (momentum)',          key: 'rsi_momentum',      extra: indicators.rsi ? `RSI ${indicators.rsi}` : '' },
    { label: 'MACD > Signal',                 key: 'macd_bullish',      extra: '' },
    { label: 'Breakout: >20D high + vol>1.5×',key: 'volume_breakout',   extra: indicators.high_20d ? `20D high ₹${indicators.high_20d}` : '' },
  ];
  const momRows = [
    { label: 'Near 52W high (within 10%)',  key: 'near_52w_high',      extra: indicators.high_52w ? `52W ₹${indicators.high_52w}` : '' },
    { label: 'Full EMA stack aligned',      key: 'ema_fully_aligned',  extra: 'Price>EMA20>EMA50>EMA200' },
    { label: 'RSI expanding (+5 in 5d)',    key: 'rsi_expanding',      extra: '' },
    { label: 'MACD histogram rising',       key: 'macd_hist_rising',   extra: '' },
  ];
  return (
    <>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} style={{ borderBottom: '1px solid #f1f5f9' }}>
              <td style={{ padding: '0.22rem 0.5rem', color: '#374151' }}>{r.label}</td>
              <td style={{ padding: '0.22rem 0.5rem', textAlign: 'center' }}><CheckIcon pass={bd[r.key]} /></td>
              <td style={{ padding: '0.22rem 0.5rem', color: '#94a3b8' }}>{r.extra}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {Object.keys(mom).length > 0 && (
        <>
          <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', margin: '0.4rem 0 0.1rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Momentum Signals (informational)
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem' }}>
            <tbody>
              {momRows.map(r => (
                <tr key={r.key} style={{ borderBottom: '1px solid #f8fafc' }}>
                  <td style={{ padding: '0.18rem 0.5rem', color: '#64748b' }}>{r.label}</td>
                  <td style={{ padding: '0.18rem 0.5rem', textAlign: 'center' }}><CheckIcon pass={mom[r.key]} /></td>
                  <td style={{ padding: '0.18rem 0.5rem', color: '#94a3b8' }}>{r.extra}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

function FundBreakdown({ bd = {}, fundamentals: f }) {
  const na = <span style={{ color: '#f59e0b', fontSize: '0.68rem' }}>Missing — click Edit to add</span>;
  const val = (v, fmt) => v != null ? fmt(v) : na;

  const rows = [
    { label: 'ROE > 15%',              key: 'roe_gt_15',             extra: val(f?.roe,              v => `${v}%`) },
    { label: 'ROCE > 15%',             key: 'roce_gt_15',            extra: val(f?.roce,             v => `${v}%`) },
    { label: 'Profit growth > 10%',    key: 'profit_growth_gt_10',   extra: val(f?.profit_growth,    v => `${v}%`) },
    { label: 'Revenue growth > 10%',   key: 'revenue_growth_gt_10',  extra: val(f?.revenue_growth,   v => `${v}%`) },
    { label: 'Debt/Equity < 0.5',      key: 'debt_equity_lt_0_5',    extra: val(f?.debt_equity,      v => `D/E ${v}`) },
    { label: 'Promoter holding stable',key: 'promoter_stable',       extra: val(f?.promoter_holding_change, v => `Δ ${v}pp QoQ`) },
    { label: 'Piotroski score ≥ 7',    key: 'piotroski_gte_7',       extra: val(f?.piotroski_score,  v => `${v}/9`) },
    { label: 'P/E < Industry P/E',     key: 'pe_below_industry',     extra: f?.pe_ratio != null && f?.industry_pe != null
        ? `${f.pe_ratio} vs Ind ${f.industry_pe}` : na },
  ];

  const extras = [
    f?.promoter_holding        != null && `Promoter: ${f.promoter_holding}%`,
    f?.operating_margin        != null && `OPM: ${f.operating_margin}%`,
    f?.eps_growth              != null && `EPS growth: ${f.eps_growth}%`,
    f?.fii_change              != null && `FII Δ: ${f.fii_change > 0 ? '+' : ''}${f.fii_change}pp`,
    f?.price_to_book           != null && f?.industry_pbv != null && `P/BV ${f.price_to_book} vs Ind ${f.industry_pbv}`,
    f?.promoter_holding_change_3y != null && `Promo 3yr Δ: ${f.promoter_holding_change_3y}pp`,
  ].filter(Boolean);

  // Count missing scored fields
  const missing = rows.filter(r => bd[r.key] === undefined || bd[r.key] === null).length;

  return (
    <>
      {missing > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 5, padding: '0.3rem 0.6rem', marginBottom: '0.4rem', fontSize: '0.72rem', color: '#92400e' }}>
          ⚠ {missing} scored fields missing — click <strong>Edit / AI Fetch</strong> to fill them
        </div>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
        <tbody>
          {rows.map(r => (
            <tr key={r.key} style={{ borderBottom: '1px solid #f1f5f9' }}>
              <td style={{ padding: '0.22rem 0.5rem', color: '#374151' }}>{r.label}</td>
              <td style={{ padding: '0.22rem 0.5rem', textAlign: 'center' }}><CheckIcon pass={bd[r.key]} /></td>
              <td style={{ padding: '0.22rem 0.5rem', fontSize: '0.72rem' }}>{r.extra}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {extras.length > 0 && (
        <div style={{ marginTop: '0.35rem', padding: '0.3rem 0.5rem', background: '#f8fafc', borderRadius: 5, fontSize: '0.7rem', color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {extras.map((e, i) => <span key={i}>• {e}</span>)}
        </div>
      )}
    </>
  );
}

// ── Fundamentals editor + AI fetch modal ─────────────────────────────────────

function FundamentalsEditor({ symbol, existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    roe:                     existing?.roe                     ?? '',
    roce:                    existing?.roce                    ?? '',
    profit_growth:           existing?.profit_growth           ?? '',
    revenue_growth:          existing?.revenue_growth          ?? '',
    debt_equity:             existing?.debt_equity             ?? '',
    pe_ratio:                existing?.pe_ratio                ?? '',
    industry_pe:             existing?.industry_pe             ?? '',
    operating_margin:        existing?.operating_margin        ?? '',
    eps_growth:              existing?.eps_growth              ?? '',
    promoter_holding:        existing?.promoter_holding        ?? '',
    promoter_holding_change: existing?.promoter_holding_change ?? '',
    piotroski_score:         existing?.piotroski_score         ?? '',
    price_to_book:           existing?.price_to_book           ?? '',
    fii_change:              existing?.fii_change              ?? '',
    sector:                  existing?.sector                  ?? '',
    notes:                   existing?.notes                   ?? '',
  });
  const [saving,    setSaving]    = useState(false);
  const [fetching,  setFetching]  = useState(false);
  const [err,       setErr]       = useState(null);
  const [aiCost,    setAiCost]    = useState(null);

  const handleSave = async () => {
    setSaving(true); setErr(null);
    const payload = {};
    Object.entries(form).forEach(([k, v]) => {
      if (v !== '') payload[k] = (k === 'sector' || k === 'notes') ? v : Number(v);
    });
    try {
      const res = await authFetch(`/api/portfolio/fundamentals/${symbol}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      onSaved();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const handleAiFetch = async () => {
    setFetching(true); setErr(null); setAiCost(null);
    try {
      const res = await authFetch(`/api/portfolio/fundamentals/fetch-ai/${symbol}`, { method: 'POST' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || await res.text());
      }
      const { data, cost_usd } = await res.json();
      // Pre-fill form with AI-extracted values (only non-null)
      setForm(f => {
        const updated = { ...f };
        Object.entries(data).forEach(([k, v]) => {
          if (v !== null && v !== undefined && k in updated) {
            updated[k] = String(v);
          }
        });
        return updated;
      });
      setAiCost(cost_usd);
    } catch (e) { setErr(e.message); }
    finally { setFetching(false); }
  };

  const field = (label, key, hint = '') => (
    <div style={{ marginBottom: '0.55rem' }}>
      <label style={{ fontSize: '0.73rem', color: '#475569', display: 'block', marginBottom: 2 }}>
        {label} {hint && <span style={{ color: '#94a3b8' }}>({hint})</span>}
      </label>
      <input
        type={key === 'sector' || key === 'notes' ? 'text' : 'number'}
        step="0.01"
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        style={{ width: '100%', padding: '0.32rem 0.5rem', fontSize: '0.82rem', border: '1px solid #cbd5e1', borderRadius: 5, boxSizing: 'border-box' }}
      />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: '1.25rem', width: 460, maxWidth: '95vw', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.85rem' }}>
          <h3 style={{ margin: 0, fontSize: '1rem', color: '#0f172a' }}>Fundamentals — {symbol}</h3>
          <button
            onClick={handleAiFetch}
            disabled={fetching}
            title="Extract from cached screener.in Excel using Claude AI"
            style={{ padding: '0.35rem 0.75rem', background: fetching ? '#e0e7ff' : '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            {fetching ? '⏳ Fetching…' : '🤖 AI Fetch'}
          </button>
        </div>

        {aiCost !== null && (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '0.4rem 0.7rem', marginBottom: '0.75rem', fontSize: '0.75rem', color: '#166534' }}>
            ✓ AI extracted data (cost: ${aiCost} USD). Review & save below.
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
          {field('ROE (%)',              'roe',              '> 15 = good')}
          {field('ROCE (%)',             'roce',             '> 15 = good')}
          {field('Profit Growth (%)',    'profit_growth',    'YoY')}
          {field('Revenue Growth (%)',   'revenue_growth',   'YoY')}
          {field('Debt / Equity',        'debt_equity',      '< 0.5 = good')}
          {field('Operating Margin (%)', 'operating_margin', 'OPM')}
          {field('P/E Ratio',            'pe_ratio')}
          {field('Industry P/E',         'industry_pe')}
          {field('Piotroski Score',      'piotroski_score',  '0–9')}
          {field('Price to Book',        'price_to_book')}
          {field('EPS Growth (%)',       'eps_growth',       'YoY')}
          {field('FII Change (pp)',      'fii_change',       'QoQ')}
          {field('Promoter Holding (%)', 'promoter_holding')}
          {field('Promoter Δ (pp)',      'promoter_holding_change', 'QoQ')}
        </div>
        {field('Sector', 'sector')}
        {field('Notes',  'notes')}

        {err && <p style={{ color: '#dc2626', fontSize: '0.78rem', margin: '0.4rem 0' }}>{err}</p>}

        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.85rem' }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, padding: '0.5rem', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onClose}
            style={{ flex: 1, padding: '0.5rem', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: '0.85rem' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Analysis row ──────────────────────────────────────────────────────────────

function AnalysisRow({ row, isMobile, onEditFundamentals }) {
  const [expanded, setExpanded] = useState(false);
  const fmt  = (n, d = 2) => n != null ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
  const pnlC = Number(row.pnl || 0) >= 0 ? '#16a34a' : '#dc2626';

  const breakdown = row.score_breakdown || {};
  const techBd    = breakdown.tech || row.tech_breakdown || {};
  const fundBd    = breakdown.fund || row.fund_breakdown || {};
  const momSig    = row.momentum_signals || {};

  return (
    <>
      <tr onClick={() => setExpanded(e => !e)}
        style={{ cursor: 'pointer', borderBottom: '1px solid #f1f5f9' }}
        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
        onMouseLeave={e => e.currentTarget.style.background = ''}>

        <td style={{ padding: '0.6rem 0.75rem', fontWeight: 700, fontSize: '0.88rem', color: '#0f172a', whiteSpace: 'nowrap' }}>
          {row.symbol}
          {row.fundamentals?.sector && (
            <div style={{ fontSize: '0.67rem', color: '#94a3b8', fontWeight: 400 }}>{row.fundamentals.sector}</div>
          )}
          {/* Momentum badge */}
          {momSig.near_52w_high && (
            <div style={{ fontSize: '0.62rem', color: '#0369a1', fontWeight: 700, marginTop: 1 }}>📈 Near 52W High</div>
          )}
        </td>

        {!isMobile && (
          <td style={{ padding: '0.6rem 0.75rem', fontSize: '0.82rem', color: '#475569', textAlign: 'right' }}>
            <div>{row.quantity} qty</div>
            <div style={{ color: '#94a3b8', fontSize: '0.72rem' }}>Avg ₹{fmt(row.average_price)}</div>
          </td>
        )}

        <td style={{ padding: '0.6rem 0.75rem', fontSize: '0.82rem', textAlign: 'right' }}>
          <div style={{ fontWeight: 600 }}>₹{fmt(row.last_price)}</div>
          <div style={{ color: pnlC, fontSize: '0.72rem', fontWeight: 600 }}>
            {Number(row.pnl || 0) >= 0 ? '+' : ''}₹{fmt(row.pnl)}
          </div>
        </td>

        {!isMobile && (
          <>
            <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontSize: '0.82rem' }}>
              <div style={{ fontWeight: 700, color: '#0369a1' }}>{row.tech_score ?? '—'}<span style={{ color: '#94a3b8', fontWeight: 400 }}>/7</span></div>
            </td>
            <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontSize: '0.82rem' }}>
              <div style={{ fontWeight: 700, color: '#7c3aed' }}>{row.fund_score ?? '—'}<span style={{ color: '#94a3b8', fontWeight: 400 }}>/9</span></div>
            </td>
            <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center', fontSize: '0.9rem', fontWeight: 800 }}>
              {row.total_score ?? '—'}<span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.75rem' }}>/16</span>
            </td>
          </>
        )}

        <td style={{ padding: '0.6rem 0.75rem', textAlign: 'center' }}><RecBadge rec={row.recommendation} /></td>
        <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>
          {expanded ? '▲' : '▼'}
        </td>
      </tr>

      {expanded && (
        <tr style={{ background: '#f8fafc' }}>
          <td colSpan={isMobile ? 4 : 8} style={{ padding: '0.75rem 1rem 1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '1rem' }}>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0369a1' }}>Technical Analysis</span>
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>RSI {row.rsi != null ? Number(row.rsi).toFixed(1) : '—'}</span>
                </div>
                <ScoreBar label="Technical Score" score={row.tech_score || 0} max={7} color="#0ea5e9" />
                <TechBreakdown
                  bd={techBd}
                  mom={momSig}
                  indicators={{ ema50: row.ema50, ema200: row.ema200, high_20d: row.high_20d, high_52w: row.high_52w, rsi: row.rsi != null ? Number(row.rsi).toFixed(1) : null }}
                />
              </div>

              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: '#7c3aed' }}>Fundamental Analysis</span>
                  <button
                    onClick={e => { e.stopPropagation(); onEditFundamentals(row); }}
                    style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', border: '1px solid #c4b5fd', borderRadius: 4, color: '#7c3aed', background: '#f5f3ff', cursor: 'pointer' }}>
                    {row.fundamentals ? 'Edit / AI Fetch' : 'Add Data / AI Fetch'}
                  </button>
                </div>
                <ScoreBar label="Fundamental Score" score={row.fund_score || 0} max={10} color="#7c3aed" />
                <FundBreakdown bd={fundBd} fundamentals={row.fundamentals} />
              </div>
            </div>

            <div style={{ marginTop: '0.75rem', paddingTop: '0.7rem', borderTop: '1px solid #e2e8f0' }}>
              <ScoreBar
                label={`Total Score → ${row.recommendation}`}
                score={row.total_score || 0} max={17}
                color={REC_STYLE[row.recommendation]?.border || '#94a3b8'}
              />
              <p style={{ fontSize: '0.68rem', color: '#94a3b8', margin: '0.3rem 0 0' }}>
                15–17 = Add More · 11–14 = Hold · 6–10 = Trim · 0–5 = Exit
              </p>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Summary cards ─────────────────────────────────────────────────────────────

function SummaryCards({ results }) {
  const counts = { 'ADD MORE': 0, HOLD: 0, TRIM: 0, EXIT: 0 };
  results.forEach(r => { if (counts[r.recommendation] !== undefined) counts[r.recommendation]++; });
  const missingFund = results.filter(r => !r.fundamentals || r.fund_score === 0).length;

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.6rem', marginBottom: '0.7rem' }}>
        {Object.entries(counts).map(([rec, cnt]) => {
          const s = REC_STYLE[rec];
          return (
            <div key={rec} style={{ background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: '0.6rem 0.75rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1.4rem', fontWeight: 800, color: s.color }}>{cnt}</div>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, color: s.color, letterSpacing: '0.04em' }}>{rec}</div>
            </div>
          );
        })}
      </div>
      {missingFund > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, padding: '0.5rem 0.85rem', fontSize: '0.78rem', color: '#92400e', marginBottom: '0.7rem' }}>
          ⚠ <strong>{missingFund} stock{missingFund > 1 ? 's' : ''}</strong> missing fundamental data — scores may be underestimated.
          Click any row → <em>AI Fetch</em> to auto-fill from screener.in Excel.
        </div>
      )}
    </>
  );
}

// ── AI Analysis components ────────────────────────────────────────────────────

const ACTION_STYLE = {
  ACCUMULATE_NOW:  { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
  BUY_ON_DIPS:     { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
  HOLD_STRONG:     { bg: '#fef9c3', color: '#854d0e', border: '#fde047' },
  HOLD_CAUTIOUS:   { bg: '#ffedd5', color: '#9a3412', border: '#fdba74' },
  REDUCE_ON_RALLY: { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  AVOID_ADDING:    { bg: '#fee2e2', color: '#7f1d1d', border: '#f87171' },
  FRESH_BUY:       { bg: '#f3e8ff', color: '#7c3aed', border: '#c4b5fd' },
};

function ActionBadge({ action }) {
  const s = ACTION_STYLE[action] || { bg: '#f1f5f9', color: '#475569', border: '#cbd5e1' };
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      borderRadius: 5, padding: '0.18rem 0.5rem',
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.02em', whiteSpace: 'nowrap',
    }}>
      {(action || '—').replace(/_/g, ' ')}
    </span>
  );
}

function Stars({ n }) {
  return (
    <span title={`Conviction: ${n}/5`}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ color: i <= n ? '#f59e0b' : '#e2e8f0', fontSize: '0.78rem' }}>★</span>
      ))}
    </span>
  );
}

function PriceLevelRow({ label, value, color }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.73rem', padding: '0.15rem 0', borderBottom: '1px solid #f8fafc' }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <span style={{ fontWeight: 600, color: color || '#0f172a' }}>₹{Number(value).toLocaleString('en-IN')}</span>
    </div>
  );
}

function AiHoldingCard({ h }) {
  const [open, setOpen] = useState(false);
  const isMobile = window.innerWidth <= 600;
  const pnlColor = (h.pnl || 0) >= 0 ? '#16a34a' : '#dc2626';
  const fmt = (n, d = 2) => n != null ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
  const pl  = h.price_levels || {};
  const an  = h.analysis || {};
  const fc  = h.if_deploying_fresh_capital || {};

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: isMobile ? '0.55rem 0.65rem' : '0.7rem 0.9rem', cursor: 'pointer' }}
        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
        onMouseLeave={e => e.currentTarget.style.background = ''}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a' }}>{h.symbol}</span>
            {h.sector && <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{h.sector}</span>}
            {an.thesis_broken && (
              <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#dc2626', background: '#fee2e2', borderRadius: 3, padding: '0 4px' }}>
                THESIS BROKEN
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 2 }}>
            <Stars n={h.conviction || 0} />
            <span style={{ fontSize: '0.7rem', color: '#64748b' }}>{h.qty} qty · avg ₹{fmt(h.avg_cost)}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>₹{fmt(h.ltp)}</div>
          <div style={{ fontSize: '0.72rem', fontWeight: 600, color: pnlColor }}>
            {(h.pnl || 0) >= 0 ? '+' : ''}₹{fmt(h.pnl)} ({(h.pnl_percent || 0) >= 0 ? '+' : ''}{Number(h.pnl_percent || 0).toFixed(1)}%)
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, marginLeft: 8 }}>
          <ActionBadge action={h.action} />
          <span style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{open ? '▲' : '▼'}</span>
        </div>
      </div>

      {open && (
        <div style={{ borderTop: '1px solid #f1f5f9', padding: '0.75rem 0.9rem', background: '#f8fafc' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.9rem', marginBottom: '0.75rem' }}>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#0369a1', marginBottom: 4 }}>Price Levels</div>
              <PriceLevelRow label="Strong support"       value={pl.strong_support}       color="#16a34a" />
              <PriceLevelRow label="Immediate support"    value={pl.immediate_support}    color="#22c55e" />
              <PriceLevelRow label="Immediate resistance" value={pl.immediate_resistance} color="#f59e0b" />
              <PriceLevelRow label="Strong resistance"    value={pl.strong_resistance}    color="#dc2626" />
              <PriceLevelRow label="Ideal entry low"      value={pl.ideal_buy_range_low}  color="#0369a1" />
              <PriceLevelRow label="Ideal entry high"     value={pl.ideal_buy_range_high} color="#0369a1" />
              <PriceLevelRow label="Stop loss"            value={pl.stop_loss}            color="#dc2626" />
              <PriceLevelRow label="Target 6m"            value={pl.target_6m}            color="#7c3aed" />
              <PriceLevelRow label="Target 12m"           value={pl.target_12m}           color="#7c3aed" />
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#7c3aed', marginBottom: 4 }}>Fresh Capital</div>
              {fc.suggested_amount > 0 && (
                <div style={{ fontSize: '0.78rem', color: '#0f172a', fontWeight: 600, marginBottom: 2 }}>
                  Deploy ₹{Number(fc.suggested_amount).toLocaleString('en-IN')}
                </div>
              )}
              {fc.tranche && (
                <div style={{ fontSize: '0.7rem', color: '#7c3aed', fontWeight: 600, marginBottom: 4 }}>
                  {fc.tranche.replace(/_/g, ' ')}
                </div>
              )}
              {fc.deploy_condition && (
                <div style={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.4 }}>
                  Condition: {fc.deploy_condition}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
            {an.technical_summary && (
              <div>
                <div style={{ fontSize: '0.67rem', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Technical</div>
                <p style={{ fontSize: '0.74rem', color: '#374151', margin: 0, lineHeight: 1.5 }}>{an.technical_summary}</p>
              </div>
            )}
            {an.fundamental_summary && (
              <div>
                <div style={{ fontSize: '0.67rem', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Fundamental</div>
                <p style={{ fontSize: '0.74rem', color: '#374151', margin: 0, lineHeight: 1.5 }}>{an.fundamental_summary}</p>
              </div>
            )}
            {an.macro_geo_impact && (
              <div>
                <div style={{ fontSize: '0.67rem', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Macro / Geo</div>
                <p style={{ fontSize: '0.74rem', color: '#374151', margin: 0, lineHeight: 1.5 }}>{an.macro_geo_impact}</p>
              </div>
            )}
            {an.recent_news && (
              <div>
                <div style={{ fontSize: '0.67rem', fontWeight: 700, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>Recent News</div>
                <p style={{ fontSize: '0.74rem', color: '#374151', margin: 0, lineHeight: 1.5 }}>{an.recent_news}</p>
              </div>
            )}
          </div>

          {(an.bull_case || an.bear_case) && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.5rem' }}>
              {an.bull_case && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '0.4rem 0.55rem' }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#15803d', marginBottom: 2 }}>BULL CASE</div>
                  <p style={{ fontSize: '0.72rem', color: '#166534', margin: 0, lineHeight: 1.4 }}>{an.bull_case}</p>
                </div>
              )}
              {an.bear_case && (
                <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 6, padding: '0.4rem 0.55rem' }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#be123c', marginBottom: 2 }}>BEAR CASE</div>
                  <p style={{ fontSize: '0.72rem', color: '#9f1239', margin: 0, lineHeight: 1.4 }}>{an.bear_case}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AiAnalysisTab() {
  const isMobileAi = window.innerWidth <= 600;
  const [liquid,    setLiquid]   = useState('');
  const [jobId,     setJobId]    = useState(null);
  const [status,    setStatus]   = useState(null); // null | 'loading' | 'pending' | 'done' | 'error'
  const [result,    setResult]   = useState(null);
  const [error,     setError]    = useState(null);
  const [elapsed,   setElapsed]  = useState(0);
  const [cachedAt,  setCachedAt] = useState(null);
  const [batchMsg,  setBatchMsg] = useState('');
  const pollRef    = useRef(null);
  const elapsedRef = useRef(null);

  // Load last cached result on mount
  useEffect(() => {
    setStatus('loading');
    authFetch('/api/portfolio/ai-analysis/latest')
      .then(r => r.json())
      .then(data => {
        if (data.result) {
          setResult(data.result);
          setCachedAt(data.analyzed_at);
          setStatus('done');
        } else {
          setStatus(null);
        }
      })
      .catch(() => setStatus(null));
  }, []);

  useEffect(() => () => {
    if (pollRef.current)    clearInterval(pollRef.current);
    if (elapsedRef.current) clearInterval(elapsedRef.current);
  }, []);

  const startAnalysis = async () => {
    if (pollRef.current)    clearInterval(pollRef.current);
    if (elapsedRef.current) clearInterval(elapsedRef.current);
    setStatus('pending'); setResult(null); setError(null); setElapsed(0); setCachedAt(null);

    let sec = 0;
    elapsedRef.current = setInterval(() => { sec++; setElapsed(sec); }, 1000);

    try {
      const res = await authFetch(
        `/api/portfolio/ai-analysis/run?liquid_amount=${Number(liquid) || 0}`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(await res.text());
      const { job_id } = await res.json();
      setJobId(job_id);

      pollRef.current = setInterval(async () => {
        try {
          const sr = await authFetch(`/api/portfolio/ai-analysis/status/${job_id}`);
          const data = await sr.json();
          if (data.message) setBatchMsg(data.message);
          if (data.status === 'done') {
            clearInterval(pollRef.current);
            clearInterval(elapsedRef.current);
            setStatus('done');
            setResult(data.result);
          } else if (data.status === 'error') {
            clearInterval(pollRef.current);
            clearInterval(elapsedRef.current);
            setStatus('error');
            setError(data.error);
          }
        } catch (_) {}
      }, 4000);
    } catch (e) {
      clearInterval(elapsedRef.current);
      setStatus('error');
      setError(e.message);
    }
  };

  const meta = result?.analysis_metadata  || {};
  const sum  = result?.portfolio_summary  || {};
  const fmt  = (n, d = 0) => n != null ? Number(n).toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }) : '—';
  const pnlC = (sum.total_pnl || 0) >= 0 ? '#16a34a' : '#dc2626';

  const PHASE_COLOR  = { BULL: '#15803d', CORRECTION: '#9a3412', BEAR: '#991b1b', SIDEWAYS: '#475569' };
  const RISK_COLOR   = { LOW: '#15803d', MEDIUM: '#9a3412', HIGH: '#dc2626', EXTREME: '#7f1d1d' };
  const HEALTH_COLOR = { STRONG: '#15803d', FAIR: '#9a3412', WEAK: '#dc2626', CRITICAL: '#7f1d1d' };

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div>
          <label style={{ fontSize: '0.72rem', color: '#64748b', display: 'block', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Liquid Capital (₹)
          </label>
          <input
            type="number"
            placeholder="e.g. 100000"
            value={liquid}
            onChange={e => setLiquid(e.target.value)}
            style={{ padding: '0.38rem 0.6rem', fontSize: '0.88rem', border: '1px solid #cbd5e1', borderRadius: 6, width: 155 }}
          />
        </div>
        <button
          onClick={startAnalysis}
          disabled={status === 'pending'}
          style={{
            padding: '0.42rem 1.2rem',
            background: status === 'pending' ? '#bae6fd' : '#0ea5e9',
            color: '#fff', border: 'none', borderRadius: 6,
            cursor: status === 'pending' ? 'not-allowed' : 'pointer',
            fontWeight: 700, fontSize: '0.88rem',
          }}
        >
          {status === 'pending'
            ? `Analysing… ${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, '0')}`
            : 'Run AI Analysis'}
        </button>
        {status === 'loading' && (
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Loading cached result…</span>
        )}
        {status === 'pending' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
              Analysing in batches of 5 · 15s cooldown between batches.
              Typical: ~1.5 min (15 stocks) · ~2.5 min (30 stocks) · ~4.5 min (60 stocks).
            </span>
            {batchMsg && (
              <span style={{ fontSize: '0.72rem', color: '#38bdf8', fontFamily: 'monospace' }}>
                ▶ {batchMsg}
              </span>
            )}
          </div>
        )}
      </div>

      {status === 'error' && error && (
        <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 7, padding: '0.7rem 1rem', color: '#991b1b', fontSize: '0.82rem', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {status === 'done' && result && (
        <>
          {/* Data quality warning */}
          {(() => {
            const dq = result.data_quality || {};
            const fundCov = dq.fundamentals_coverage || 0;
            const total   = dq.total_stocks || 1;
            const pct     = Math.round((fundCov / total) * 100);
            if (pct < 50) return (
              <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 7, padding: '0.5rem 0.8rem', fontSize: '0.78rem', color: '#92400e', marginBottom: '0.7rem', display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1rem' }}>⚠️</span>
                <span>
                  <strong>Low data quality:</strong> Fundamental data (PE, ROE, debt) is available for only {fundCov}/{total} stocks ({pct}%).
                  Recommendations may be less accurate. Run <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>python fetch_fundamentals.py</code> on the server to populate it.
                </span>
              </div>
            );
            return null;
          })()}
          {/* Market context banner */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(115px, 1fr))', gap: '0.45rem', marginBottom: '0.7rem' }}>
            {[
              { label: 'Market Phase',  value: meta.market_phase,            color: PHASE_COLOR[meta.market_phase]              },
              { label: 'Geo Risk',      value: meta.geopolitical_risk_level,  color: RISK_COLOR[meta.geopolitical_risk_level]    },
              meta.nifty_at_analysis > 0  && { label: 'Nifty 50',  value: fmt(meta.nifty_at_analysis),                         color: '#0369a1' },
              meta.india_vix > 0          && { label: 'India VIX', value: Number(meta.india_vix).toFixed(1),                    color: meta.india_vix > 20 ? '#dc2626' : '#0369a1' },
              meta.brent_crude_usd > 0    && { label: 'Brent $/bbl', value: `$${Number(meta.brent_crude_usd).toFixed(0)}`,     color: '#b45309' },
              meta.usdinr > 0             && { label: 'USD/INR',   value: Number(meta.usdinr).toFixed(1),                      color: '#0f172a' },
            ].filter(Boolean).map((item, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.5rem 0.65rem', textAlign: 'center' }}>
                <div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontSize: '0.85rem', fontWeight: 800, color: item.color || '#0f172a' }}>{item.value}</div>
              </div>
            ))}
          </div>
          {meta.geopolitical_summary && (
            <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 7, padding: '0.4rem 0.8rem', fontSize: '0.78rem', color: '#9a3412', marginBottom: '0.8rem' }}>
              🌍 {meta.geopolitical_summary}
            </div>
          )}

          {/* Portfolio summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.45rem', marginBottom: '0.7rem' }}>
            {[
              { label: 'Invested',      value: `₹${fmt(sum.total_invested)}`,                                                                                  color: '#0f172a' },
              { label: 'Current Value', value: `₹${fmt(sum.current_value)}`,                                                                                   color: '#0f172a' },
              { label: 'Total P&L',     value: `${(sum.total_pnl||0)>=0?'+':''}₹${fmt(sum.total_pnl)} (${(sum.pnl_percent||0)>=0?'+':''}${Number(sum.pnl_percent||0).toFixed(1)}%)`, color: pnlC },
              sum.overall_portfolio_health && { label: 'Health', value: sum.overall_portfolio_health, color: HEALTH_COLOR[sum.overall_portfolio_health] || '#475569' },
            ].filter(Boolean).map((item, i) => (
              <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.5rem 0.65rem' }}>
                <div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>{item.label}</div>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>
          {(sum.top_risk || sum.top_opportunity) && (
            <div style={{ display: 'grid', gridTemplateColumns: isMobileAi ? '1fr' : '1fr 1fr', gap: '0.5rem', marginBottom: '0.8rem' }}>
              {sum.top_risk && (
                <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', borderRadius: 7, padding: '0.5rem 0.7rem' }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#be123c', textTransform: 'uppercase', marginBottom: 2 }}>Top Risk</div>
                  <p style={{ fontSize: '0.75rem', color: '#9f1239', margin: 0, lineHeight: 1.4 }}>{sum.top_risk}</p>
                </div>
              )}
              {sum.top_opportunity && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '0.5rem 0.7rem' }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 700, color: '#15803d', textTransform: 'uppercase', marginBottom: 2 }}>Top Opportunity</div>
                  <p style={{ fontSize: '0.75rem', color: '#166534', margin: 0, lineHeight: 1.4 }}>{sum.top_opportunity}</p>
                </div>
              )}
            </div>
          )}

          {/* Holdings analysis */}
          {result.holdings_analysis?.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
                Holdings Analysis ({result.holdings_analysis.length} stocks)
              </div>
              <div style={{ display: 'grid', gap: '0.45rem' }}>
                {result.holdings_analysis.map(h => <AiHoldingCard key={h.symbol} h={h} />)}
              </div>
            </div>
          )}

          {/* Fresh opportunities */}
          {result.fresh_opportunities?.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#a78bfa', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
                Fresh Buy Opportunities
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: '0.55rem' }}>
                {result.fresh_opportunities.map((o, i) => {
                  const pl = o.price_levels || {};
                  return (
                    <div key={i} style={{ background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: 10, padding: '0.75rem 0.9rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5 }}>
                        <div>
                          <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a' }}>{o.symbol}</span>
                          {o.sector && <span style={{ fontSize: '0.65rem', color: '#94a3b8', marginLeft: 6 }}>{o.sector}</span>}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
                          <ActionBadge action="FRESH_BUY" />
                          <Stars n={o.conviction || 0} />
                        </div>
                      </div>
                      {o.why_now && <p style={{ fontSize: '0.74rem', color: '#4c1d95', margin: '0 0 5px', lineHeight: 1.4 }}>{o.why_now}</p>}
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>
                        {pl.current_price > 0 && <div>CMP ₹{Number(pl.current_price).toLocaleString('en-IN')} · Entry ₹{Number(pl.ideal_entry_low).toLocaleString('en-IN')}–{Number(pl.ideal_entry_high).toLocaleString('en-IN')}</div>}
                        {pl.target_12m > 0 && <div>Target 12m ₹{Number(pl.target_12m).toLocaleString('en-IN')} · SL ₹{Number(pl.stop_loss).toLocaleString('en-IN')}</div>}
                        {o.suggested_amount > 0 && <div style={{ marginTop: 3, fontWeight: 600, color: '#7c3aed' }}>₹{Number(o.suggested_amount).toLocaleString('en-IN')} · {(o.tranche || '').replace(/_/g, ' ')}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Deployment plan */}
          {result.deployment_plan?.tranches?.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
                Capital Deployment Plan
                <span style={{ fontSize: '0.74rem', fontWeight: 400, color: '#64748b', marginLeft: 8 }}>
                  Deploy ₹{fmt(result.deployment_plan.total_to_deploy)} · Buffer ₹{fmt(result.deployment_plan.buffer_to_retain)}
                </span>
              </div>
              <div style={{ display: 'grid', gap: '0.45rem' }}>
                {result.deployment_plan.tranches.map(t => (
                  <div key={t.tranche_id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '0.6rem 0.85rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: t.allocations?.length ? 4 : 0 }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: '0.78rem', color: '#0369a1' }}>{t.tranche_id}</span>
                        <span style={{ fontSize: '0.78rem', color: '#475569', marginLeft: 8 }}>{t.label}</span>
                      </div>
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>₹{fmt(t.amount)}</span>
                    </div>
                    {t.trigger && <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '2px 0 4px' }}>{t.trigger}</p>}
                    {t.allocations?.length > 0 && (
                      <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 5, display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {t.allocations.map((a, i) => (
                          <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 5, padding: '0.22rem 0.5rem', fontSize: '0.71rem' }}>
                            <span style={{ fontWeight: 700, color: '#0f172a' }}>{a.symbol}</span>
                            <span style={{ color: '#64748b' }}> ₹{fmt(a.amount)} · {Number(a.price_range_low).toLocaleString('en-IN')}–{Number(a.price_range_high).toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Watchlist alerts */}
          {result.watchlist_alerts?.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>
                Watchlist Alerts
              </div>
              <div style={{ display: 'grid', gap: '0.4rem' }}>
                {result.watchlist_alerts.map((a, i) => (
                  <div key={i} style={{
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                    padding: '0.55rem 0.85rem',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 800, fontSize: '0.82rem', color: '#0369a1' }}>{a.symbol}</span>
                        <span style={{ fontSize: '0.65rem', background: '#f1f5f9', color: '#475569', borderRadius: 3, padding: '1px 6px', fontWeight: 600 }}>
                          {(a.alert_type || '').replace(/_/g, ' ')}
                        </span>
                      </div>
                      {a.price_level > 0 && (
                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap', marginLeft: 6 }}>
                          ₹{Number(a.price_level).toLocaleString('en-IN')}
                        </span>
                      )}
                    </div>
                    <p style={{ margin: 0, fontSize: '0.74rem', color: '#475569', lineHeight: 1.45 }}>{a.alert_message}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '0.5rem' }}>
            {cachedAt
              ? <>Cached result from {new Date(cachedAt).toLocaleString('en-IN')}. </>
              : meta.generated_at ? <>Generated {new Date(meta.generated_at).toLocaleString('en-IN')}. </> : null}
            Claude + live web search. Not financial advice.
          </p>
        </>
      )}
    </div>
  );
}

// ── Technical Analysis Tab ────────────────────────────────────────────────────

function RsiPill({ rsi }) {
  if (rsi == null) return <span style={{ color: '#94a3b8' }}>—</span>;
  const n = Number(rsi).toFixed(1);
  const col = rsi < 30 ? '#dc2626' : rsi > 70 ? '#d97706' : rsi >= 55 ? '#16a34a' : '#0369a1';
  return <span style={{ fontWeight: 700, color: col }}>{n}</span>;
}

function EmaStatus({ row }) {
  const p = row.last_price;
  const e20 = row.ema20, e50 = row.ema50, e200 = row.ema200;
  if (!p || !e50) return <span style={{ color: '#94a3b8' }}>—</span>;
  const above200 = e200 ? p > e200 : null;
  const above50  = p > e50;
  const golden   = e200 ? e50 > e200 : null;
  return (
    <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
      <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3,
        background: above50 ? '#dcfce7' : '#fee2e2', color: above50 ? '#15803d' : '#dc2626' }}>
        {above50 ? '▲' : '▼'} EMA50
      </span>
      {above200 !== null && (
        <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3,
          background: above200 ? '#dcfce7' : '#fee2e2', color: above200 ? '#15803d' : '#dc2626' }}>
          {above200 ? '▲' : '▼'} EMA200
        </span>
      )}
      {golden !== null && golden && (
        <span style={{ fontSize: '0.65rem', fontWeight: 700, padding: '1px 5px', borderRadius: 3,
          background: '#fef9c3', color: '#854d0e' }}>✦ Golden</span>
      )}
    </div>
  );
}

// ── Mobile Tech Card ─────────────────────────────────────────────────────────

function TechCard({ row, expanded, onToggle, onEditFundamentals }) {
  const f = row.fundamentals || {};
  const bd = typeof row.tech_breakdown === 'string'
    ? JSON.parse(row.tech_breakdown || '{}') : (row.tech_breakdown || {});
  const fbd = typeof row.fund_breakdown === 'string'
    ? JSON.parse(row.fund_breakdown || '{}') : (row.fund_breakdown || {});
  const macdBull = row.macd != null && row.macd_signal != null ? row.macd > row.macd_signal : null;
  const pnlPct = row.average_price > 0 && row.last_price > 0
    ? ((row.last_price - row.average_price) / row.average_price * 100).toFixed(1) : null;
  const pnlColor = pnlPct != null ? (Number(pnlPct) >= 0 ? '#16a34a' : '#dc2626') : '#64748b';

  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: '0.5rem', overflow: 'hidden' }}>
      {/* Collapsed header */}
      <div onClick={onToggle} style={{ padding: '0.6rem 0.75rem', cursor: 'pointer' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a' }}>{row.symbol}</span>
              <RecBadge rec={row.recommendation} />
            </div>
            {f.sector && <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{f.sector}</div>}
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a' }}>{row.total_score ?? '—'}<span style={{ color: '#94a3b8', fontWeight: 400, fontSize: '0.7rem' }}>/16</span></div>
            <div style={{ fontSize: '0.65rem', color: '#64748b' }}>T:{row.tech_score} F:{row.fund_score}</div>
          </div>
        </div>
        {/* Key indicators row */}
        <div style={{ display: 'flex', gap: '0.45rem', marginTop: '0.35rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <RsiPill rsi={row.rsi} />
          {macdBull != null && (
            <span style={{ fontSize: '0.68rem', fontWeight: 700, color: macdBull ? '#16a34a' : '#dc2626' }}>
              {macdBull ? '▲ Bull' : '▼ Bear'}
            </span>
          )}
          <EmaStatus row={row} />
          {pnlPct != null && (
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: pnlColor, marginLeft: 'auto' }}>
              {Number(pnlPct) >= 0 ? '+' : ''}{pnlPct}%
            </span>
          )}
          <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ borderTop: '1px solid #f1f5f9', padding: '0.65rem 0.75rem' }}>
          {/* Technical signals */}
          <div style={{ marginBottom: '0.6rem' }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', marginBottom: '0.25rem' }}>Technical Signals</div>
            <TechBreakdown bd={bd} mom={{}} indicators={{ ema50: row.ema50, ema200: row.ema200, rsi: row.rsi != null ? Number(row.rsi).toFixed(1) : null, high_20d: row.high_20d, high_52w: row.high_52w }} />
          </div>
          {/* Fundamental signals */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase' }}>Fundamental Signals</span>
              {onEditFundamentals && (
                <button onClick={e => { e.stopPropagation(); onEditFundamentals(row); }}
                  style={{ fontSize: '0.68rem', padding: '0.12rem 0.45rem', border: '1px solid #c4b5fd', borderRadius: 4, color: '#7c3aed', background: '#f5f3ff', cursor: 'pointer' }}>
                  {row.fundamentals ? 'Edit' : 'Add Data'}
                </button>
              )}
            </div>
            <FundBreakdown bd={fbd} fundamentals={f} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Desktop Tech Row ─────────────────────────────────────────────────────────

function TechRow({ row, expanded, onToggle, onEditFundamentals }) {
  const f = row.fundamentals || {};
  const bd = typeof row.tech_breakdown === 'string'
    ? JSON.parse(row.tech_breakdown || '{}') : (row.tech_breakdown || {});
  const fbd = typeof row.fund_breakdown === 'string'
    ? JSON.parse(row.fund_breakdown || '{}') : (row.fund_breakdown || {});
  const mom = typeof row.momentum_signals === 'string'
    ? JSON.parse(row.momentum_signals || '{}') : (row.momentum_signals || {});
  const macdBull = row.macd != null && row.macd_signal != null ? row.macd > row.macd_signal : null;
  const pnlPct = row.average_price > 0 && row.last_price > 0
    ? ((row.last_price - row.average_price) / row.average_price * 100).toFixed(1) : null;

  return (
    <>
      <tr
        onClick={onToggle}
        style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer', background: expanded ? '#f8faff' : '#fff' }}
      >
        <td style={{ padding: '0.5rem 0.7rem', whiteSpace: 'nowrap' }}>
          <div style={{ fontWeight: 800, fontSize: '0.82rem', color: '#0f172a' }}>{row.symbol}</div>
          {f.sector && <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>{f.sector}</div>}
        </td>
        <td style={{ padding: '0.5rem 0.5rem' }}><RecBadge rec={row.recommendation} /></td>
        <td style={{ padding: '0.5rem 0.5rem', textAlign: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#0f172a' }}>{row.total_score ?? '—'}</div>
          <div style={{ fontSize: '0.65rem', color: '#64748b' }}>T:{row.tech_score} F:{row.fund_score}</div>
        </td>
        <td style={{ padding: '0.5rem 0.5rem' }}><RsiPill rsi={row.rsi} /></td>
        <td style={{ padding: '0.5rem 0.5rem' }}><EmaStatus row={row} /></td>
        <td style={{ padding: '0.5rem 0.5rem', textAlign: 'center' }}>
          {macdBull === null ? <span style={{ color: '#94a3b8' }}>—</span>
            : <span style={{ fontWeight: 700, color: macdBull ? '#16a34a' : '#dc2626' }}>{macdBull ? '▲ Bull' : '▼ Bear'}</span>}
        </td>
        <td style={{ padding: '0.5rem 0.5rem', textAlign: 'right', whiteSpace: 'nowrap' }}>
          {pnlPct !== null && (
            <span style={{ fontWeight: 700, fontSize: '0.8rem', color: Number(pnlPct) >= 0 ? '#16a34a' : '#dc2626' }}>
              {Number(pnlPct) >= 0 ? '+' : ''}{pnlPct}%
            </span>
          )}
        </td>
        <td style={{ padding: '0.5rem 0.4rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem' }}>
          {expanded ? '▲' : '▼'}
        </td>
      </tr>
      {expanded && (
        <tr style={{ background: '#f8faff' }}>
          <td colSpan={8} style={{ padding: '0.6rem 1rem 0.8rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0.75rem' }}>
              <div>
                <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Technical Signals</div>
                <TechBreakdown bd={bd} mom={mom} indicators={{ ema50: row.ema50, ema200: row.ema200, rsi: row.rsi != null ? Number(row.rsi).toFixed(1) : null, high_20d: row.high_20d, high_52w: row.high_52w }} />
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.3rem' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase' }}>Fundamental Signals</span>
                  {onEditFundamentals && (
                    <button
                      onClick={e => { e.stopPropagation(); onEditFundamentals(row); }}
                      style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', border: '1px solid #c4b5fd', borderRadius: 4, color: '#7c3aed', background: '#f5f3ff', cursor: 'pointer' }}>
                      {row.fundamentals ? 'Edit / AI Fetch' : 'Add Data / AI Fetch'}
                    </button>
                  )}
                </div>
                <FundBreakdown bd={fbd} fundamentals={f} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ── Swing Setup Modal ────────────────────────────────────────────────────────

function SwingSetupModal({ symbol, onClose }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [capital, setCapital] = useState(50000);
  const [entry, setEntry]     = useState('');
  const [sl, setSl]           = useState('');
  const [t1, setT1]           = useState('');
  const [t2, setT2]           = useState('');
  const [qty, setQty]         = useState('');
  const [saving, setSaving]   = useState(false);
  const [saved, setSaved]     = useState(false);

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/swing-analysis/${symbol}?capital=${capital}`)
      .then(async r => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
        return r.json();
      })
      .then(d => {
        setData(d);
        setEntry(d.entry_price);
        setSl(d.stop_loss);
        setT1(d.target_1);
        setT2(d.target_2);
        setQty(d.suggested_qty);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [symbol]);

  // Recompute qty when capital or entry changes
  useEffect(() => {
    if (entry > 0 && capital > 0) setQty(Math.max(1, Math.floor(capital / entry)));
  }, [capital, entry]);

  const risk = entry && sl ? Math.max(0, Number(entry) - Number(sl)) : 0;
  const rr = risk > 0 && t1 ? ((Number(t1) - Number(entry)) / risk).toFixed(1) : '—';

  const handleAdd = async () => {
    setSaving(true); setError(null);
    try {
      const payload = {
        symbol,
        entry_price: Number(entry) || null,
        stop_loss: Number(sl) || null,
        trail_stop_loss: Number(sl) || null,
        quantity: Number(qty) || null,
        entry_type: 'Buy',
        strategy_type: 'swing',
      };
      const r = await authFetch('/api/prebuy_watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const txt = await r.text();
      if (!r.ok) {
        let detail = txt;
        try { detail = JSON.parse(txt).detail || txt; } catch {}
        throw new Error(`HTTP ${r.status}: ${detail}`);
      }
      setSaved(true);
      setTimeout(onClose, 1200);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const fmt = v => v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
  const TREND_C = { BULLISH: '#16a34a', BEARISH: '#dc2626', NEUTRAL: '#b45309' };
  const ind = data?.indicators || {};
  const sig = data?.signals || {};

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto', padding: '1.5rem', boxShadow: '0 8px 30px rgba(0,0,0,0.15)' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 800, color: '#0f172a' }}>Swing Setup — {symbol}</h2>
            {data && (
              <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
                {data.sector || ''} · Fund: {data.fund_score}/9 · Tech: {data.tech_score}/7
              </div>
            )}
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '1.2rem', cursor: 'pointer', color: '#94a3b8' }}>✕</button>
        </div>

        {loading && <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Analysing {symbol}…</div>}
        {error && !data && <div style={{ padding: '1rem', background: '#fee2e2', borderRadius: 8, color: '#991b1b', fontSize: '0.85rem' }}>{error}</div>}

        {data && (
          <>
            {/* Signals bar */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem', fontSize: '0.75rem' }}>
              <span style={{ background: TREND_C[sig.trend] || '#64748b', color: '#fff', padding: '0.15rem 0.5rem', borderRadius: 4, fontWeight: 700 }}>{sig.trend}</span>
              <span style={{ color: '#64748b' }}>RSI <strong>{fmt(ind.rsi)}</strong></span>
              <span style={{ color: sig.macd_bullish ? '#16a34a' : '#dc2626', fontWeight: 600 }}>MACD {sig.macd_bullish ? 'Bull' : 'Bear'}</span>
              {sig.ema_aligned && <span style={{ color: '#16a34a', fontWeight: 600 }}>EMA Aligned</span>}
              {ind.adx != null && <span style={{ color: '#64748b' }}>ADX <strong>{Number(ind.adx).toFixed(0)}</strong></span>}
              {sig.volume_confirmation && <span style={{ color: '#0369a1', fontWeight: 600 }}>Vol Confirmed</span>}
              <span style={{ color: '#64748b' }}>LTP <strong>₹{fmt(data.ltp)}</strong></span>
            </div>

            {/* Price levels form */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '0.75rem' }}>
              <div>
                <label style={labelS}>Entry Price</label>
                <input type="number" step="0.05" value={entry} onChange={e => setEntry(Number(e.target.value))} style={inputS} />
              </div>
              <div>
                <label style={labelS}>Stop Loss</label>
                <input type="number" step="0.05" value={sl} onChange={e => setSl(Number(e.target.value))} style={inputS} />
              </div>
              <div>
                <label style={labelS}>Target 1 (1.5R)</label>
                <input type="number" step="0.05" value={t1} onChange={e => setT1(Number(e.target.value))} style={inputS} />
              </div>
              <div>
                <label style={labelS}>Target 2 (2.5R)</label>
                <input type="number" step="0.05" value={t2} onChange={e => setT2(Number(e.target.value))} style={inputS} />
              </div>
            </div>

            {/* Risk info */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', fontSize: '0.78rem', color: '#475569' }}>
              <span>Risk/share: <strong style={{ color: '#dc2626' }}>₹{fmt(risk)}</strong></span>
              <span>R:R <strong style={{ color: '#16a34a' }}>1:{rr}</strong></span>
              <span>SuperTrend: <strong>₹{fmt(ind.supertrend)}</strong></span>
              <span>ATR: <strong>₹{fmt(ind.atr)}</strong></span>
            </div>

            {/* Capital + Qty */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginBottom: '1rem' }}>
              <div>
                <label style={labelS}>Capital (₹)</label>
                <input type="number" value={capital} onChange={e => setCapital(Number(e.target.value))} style={inputS} />
              </div>
              <div>
                <label style={labelS}>Quantity</label>
                <input type="number" value={qty} onChange={e => setQty(Number(e.target.value))} style={inputS} />
              </div>
            </div>

            {/* Indicators summary */}
            <div style={{ background: '#f8fafc', borderRadius: 8, padding: '0.5rem 0.7rem', marginBottom: '1rem', fontSize: '0.72rem', color: '#64748b', display: 'flex', gap: '0.7rem', flexWrap: 'wrap' }}>
              <span>EMA20 ₹{fmt(ind.ema20)}</span>
              <span>EMA50 ₹{fmt(ind.ema50)}</span>
              {ind.ema200 && <span>EMA200 ₹{fmt(ind.ema200)}</span>}
              <span>Vol {Number(ind.latest_volume || 0).toLocaleString('en-IN')} / avg {Number(ind.avg_volume || 0).toLocaleString('en-IN')}</span>
            </div>

            {/* Actions */}
            {saved ? (
              <div style={{ padding: '0.75rem', background: '#dcfce7', borderRadius: 8, textAlign: 'center', color: '#15803d', fontWeight: 700 }}>
                Added to Watchlist
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={handleAdd} disabled={saving}
                  style={{ flex: 1, padding: '0.55rem', background: saving ? '#93c5fd' : '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: '0.9rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Adding…' : 'Add to Watchlist'}
                </button>
                <button onClick={onClose}
                  style={{ padding: '0.55rem 1.2rem', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, fontSize: '0.9rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            )}
            {error && data && <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#dc2626' }}>{error}</div>}
          </>
        )}
      </div>
    </div>
  );
}

const labelS = { display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 2 };
const inputS = { width: '100%', padding: '0.4rem 0.55rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.88rem', fontWeight: 600 };

// ── Other Stocks Fundamentals (non-holding) ─────────────────────────────────

function OtherStocksFundamentals({ onEditFundamentals, onAnalyse, isMobile }) {
  const [stocks, setStocks]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [filter, setFilter]     = useState('');
  const [fetchScope, setFetchScope] = useState('');
  const [fetchMsg, setFetchMsg] = useState(null);
  const [customSyms, setCustomSyms] = useState('');
  const [autoReload, setAutoReload] = useState(null);
  const [selected, setSelected]         = useState(new Set());
  const [bulkResults, setBulkResults]   = useState(null);
  const [bulkLoading, setBulkLoading]   = useState(false);
  const [bulkCapital, setBulkCapital]   = useState(50000);
  const [bulkFilter, setBulkFilter]     = useState('');

  // Auto-load on mount
  React.useEffect(() => { loadStocks(); }, []);

  const loadStocks = () => {
    setLoading(true);
    authFetch('/api/portfolio/fundamentals')
      .then(r => r.json())
      .then(data => {
        setStocks(data.other_stocks || []);
        if (autoReload && (data.other_stocks || []).length > 0) {
          setFetchMsg(prev => prev ? `${prev.split('.')[0]}. ${(data.other_stocks || []).length} stocks loaded.` : null);
        }
      })
      .catch(err => console.error('Failed to load other stocks:', err))
      .finally(() => setLoading(false));
  };

  // Auto-reload every 30s after triggering a fetch
  React.useEffect(() => {
    if (!autoReload) return;
    const timer = setInterval(loadStocks, 30000);
    return () => clearInterval(timer);
  }, [autoReload]);

  const triggerFetch = (scope) => {
    const actualScope = scope === 'custom' ? customSyms.trim() : scope;
    if (!actualScope) return;
    setFetchMsg('Fetching started… will auto-reload every 30s.');
    setAutoReload(Date.now());
    const src = fetchScope === 'stockanalysis' ? '&source=stockanalysis' : '';
    authFetch(`/api/portfolio/fundamentals/fetch?scope=${encodeURIComponent(actualScope)}${src}`, { method: 'POST' })
      .then(r => r.json())
      .then(d => setFetchMsg(`Fetch running (${d.source || 'screener'}, ${actualScope}). Auto-reloading every 30s…`))
      .catch(err => setFetchMsg(`Error: ${err.message}`));
  };

  const toggleSelect = (sym) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(sym) ? next.delete(sym) : next.add(sym);
      return next;
    });
  };

  const selectAllFiltered = () => {
    const syms = sorted.slice(0, 200).map(s => s.symbol);
    setSelected(prev => {
      const allSelected = syms.every(s => prev.has(s));
      if (allSelected) return new Set();  // deselect all
      return new Set([...prev, ...syms]);
    });
  };

  const runBulkSwingAnalysis = () => {
    const syms = [...selected];
    if (!syms.length) return;
    setBulkLoading(true);
    setBulkResults(null);
    authFetch('/api/swing-analysis/bulk?capital_per_stock=' + bulkCapital, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(syms),
    })
      .then(r => r.json())
      .then(data => setBulkResults(data.results || []))
      .catch(err => console.error('Bulk analysis failed:', err))
      .finally(() => setBulkLoading(false));
  };

  const fmt = (v, d = 1) => v != null ? Number(v).toFixed(d) : '—';

  // Compute fundamental score client-side (mirrors analysis_engine._compute_fundamental_score)
  // Piotroski shown for reference but NOT scored (max 9)
  const computeScore = (s) => {
    let score = 0;
    if (s.roe > 15) score += 2;
    if (s.profit_growth > 10) score += 2;
    if (s.revenue_growth > 10) score += 1;
    if (s.debt_equity != null && s.debt_equity < 0.5) score += 1;
    if (s.promoter_holding_change != null && s.promoter_holding_change >= 0) score += 1;
    if (s.roce > 15) score += 1;
    if (s.pe_ratio != null && s.industry_pe != null && s.pe_ratio > 0 && s.pe_ratio < s.industry_pe) score += 1;
    return score;
  };

  const [sortField, setSortField] = useState('fund_score');
  const [sortDir, setSortDir]     = useState('desc');

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  // Enrich with score, then filter, then sort
  const enriched = (stocks || []).map(s => ({ ...s, fund_score: computeScore(s) }));
  const filtered = enriched.filter(s =>
    !filter || s.symbol?.toLowerCase().includes(filter.toLowerCase()) || (s.sector || '').toLowerCase().includes(filter.toLowerCase())
  );
  const scoreColor = sc => sc >= 7 ? '#16a34a' : sc >= 5 ? '#0369a1' : sc >= 3 ? '#b45309' : '#dc2626';
  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortField] ?? -999, vb = b[sortField] ?? -999;
    if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
    return sortDir === 'asc' ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
  });

  return (
    <div id="other-stocks-section" style={{ marginTop: '2rem' }}>
      {/* Section header — always visible */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem' }}>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#0f172a' }}>
          Other Stocks {stocks ? `(${stocks.length})` : ''}
        </h2>
        <button onClick={() => { loadStocks(); setAutoReload(null); }}
          style={{ padding: '0.2rem 0.5rem', background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer' }}>
          Reload
        </button>
      </div>

      {/* Controls bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.6rem', flexWrap: 'wrap' }}>
        <input
          placeholder="Filter by symbol or sector…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          style={{ padding: '0.3rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: '0.78rem', width: 180 }}
        />
        <select value={fetchScope} onChange={e => setFetchScope(e.target.value)}
          style={{ padding: '0.28rem 0.4rem', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: '0.73rem' }}>
          <option value="">Screener.in</option>
          <option value="stockanalysis">StockAnalysis.com</option>
        </select>
        <button onClick={() => triggerFetch('missing')}
          style={{ padding: '0.25rem 0.6rem', background: '#16a34a', color: '#fff', border: 'none', borderRadius: 5, fontSize: '0.73rem', fontWeight: 600, cursor: 'pointer' }}>
          Fetch Missing
        </button>
        <button onClick={() => triggerFetch('all')}
          style={{ padding: '0.25rem 0.6rem', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 5, fontSize: '0.73rem', fontWeight: 600, cursor: 'pointer' }}>
          Fetch All
        </button>
        <input
          placeholder="INFY,TCS,RELIANCE"
          value={customSyms}
          onChange={e => setCustomSyms(e.target.value.toUpperCase())}
          style={{ padding: '0.3rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: '0.73rem', width: 140 }}
        />
        <button onClick={() => triggerFetch('custom')}
          disabled={!customSyms.trim()}
          style={{ padding: '0.25rem 0.5rem', background: customSyms.trim() ? '#7c3aed' : '#e2e8f0', color: customSyms.trim() ? '#fff' : '#94a3b8', border: 'none', borderRadius: 5, fontSize: '0.73rem', fontWeight: 600, cursor: customSyms.trim() ? 'pointer' : 'not-allowed' }}>
          Fetch
        </button>
        {fetchMsg && <span style={{ fontSize: '0.7rem', color: '#16a34a' }}>{fetchMsg}</span>}
      </div>

      {/* Score quick-select buttons */}
      {stocks && stocks.length > 0 && (
        <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: '0.72rem', color: '#64748b', marginRight: '0.2rem' }}>Select by score:</span>
          {[9, 8, 7, 6, 5].map(sc => {
            const count = sorted.filter(s => s.fund_score === sc).length;
            if (!count) return null;
            return (
              <button key={sc} onClick={() => {
                const syms = sorted.filter(s => s.fund_score === sc).map(s => s.symbol);
                setSelected(prev => {
                  const allIn = syms.every(s => prev.has(s));
                  const next = new Set(prev);
                  syms.forEach(s => allIn ? next.delete(s) : next.add(s));
                  return next;
                });
              }}
              style={{
                padding: '0.18rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer',
                background: sorted.filter(s => s.fund_score === sc).every(s => selected.has(s.symbol)) ? '#0369a1' : '#f1f5f9',
                color: sorted.filter(s => s.fund_score === sc).every(s => selected.has(s.symbol)) ? '#fff' : '#475569',
                border: '1px solid #cbd5e1',
              }}>
                {sc}/9 ({count})
              </button>
            );
          })}
          {selected.size > 0 && (
            <button onClick={() => setSelected(new Set())}
              style={{ padding: '0.18rem 0.45rem', fontSize: '0.7rem', color: '#dc2626', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 4, cursor: 'pointer' }}>
              Clear all
            </button>
          )}
        </div>
      )}

      {loading && <div style={{ color: '#94a3b8', padding: '1rem', fontSize: '0.85rem' }}>Loading…</div>}

      {/* Selection bar — shown when stocks are selected */}
      {selected.size > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.5rem 0.75rem', marginBottom: '0.5rem',
          background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0369a1' }}>
            {selected.size} selected
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <label style={{ fontSize: '0.72rem', color: '#475569' }}>Capital/stock ₹</label>
            <input type="number" value={bulkCapital} onChange={e => setBulkCapital(Number(e.target.value))}
              style={{ width: 80, padding: '0.25rem 0.4rem', border: '1px solid #93c5fd', borderRadius: 4, fontSize: '0.78rem' }} />
          </div>
          <button onClick={runBulkSwingAnalysis} disabled={bulkLoading}
            style={{ padding: '0.3rem 0.8rem', background: bulkLoading ? '#93c5fd' : '#0369a1', color: '#fff',
              border: 'none', borderRadius: 5, fontSize: '0.78rem', fontWeight: 700, cursor: bulkLoading ? 'not-allowed' : 'pointer' }}>
            {bulkLoading ? `Analysing ${selected.size} stocks…` : `Run Swing Analysis (${selected.size})`}
          </button>
          <button onClick={() => setSelected(new Set())}
            style={{ padding: '0.25rem 0.5rem', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: '0.72rem', cursor: 'pointer' }}>
            Clear
          </button>
        </div>
      )}

      {/* Bulk swing analysis results */}
      {bulkResults && bulkResults.length > 0 && (
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.4rem', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0369a1' }}>
              Swing Analysis Results ({bulkResults.filter(r => !r.error).length}/{bulkResults.length})
            </span>
            <input placeholder="Filter results…" value={bulkFilter} onChange={e => setBulkFilter(e.target.value)}
              style={{ padding: '0.25rem 0.5rem', border: '1px solid #93c5fd', borderRadius: 4, fontSize: '0.75rem', width: 150 }} />
            {['BULLISH', 'NEUTRAL', 'BEARISH'].map(t => (
              <button key={t} onClick={() => setBulkFilter(bulkFilter === t ? '' : t)}
                style={{ fontSize: '0.65rem', padding: '0.12rem 0.4rem', borderRadius: 3, cursor: 'pointer', fontWeight: 600,
                  background: bulkFilter === t ? (t === 'BULLISH' ? '#16a34a' : t === 'BEARISH' ? '#dc2626' : '#b45309') : '#f1f5f9',
                  color: bulkFilter === t ? '#fff' : '#475569', border: '1px solid #e2e8f0' }}>{t}</button>
            ))}
            <button onClick={() => { setBulkResults(null); setBulkFilter(''); }}
              style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', border: '1px solid #e2e8f0', borderRadius: 4, color: '#64748b', background: '#f8fafc', cursor: 'pointer', marginLeft: 'auto' }}>
              Close
            </button>
          </div>
          {(() => {
            const filteredResults = bulkResults.filter(r => !r.error).filter(r => {
              if (!bulkFilter) return true;
              const q = bulkFilter.toLowerCase();
              return r.symbol?.toLowerCase().includes(q) || r.signals?.trend?.toLowerCase().includes(q) || r.sector?.toLowerCase().includes(q);
            });
            const TREND_BG = { BULLISH: '#dcfce7', BEARISH: '#fee2e2', NEUTRAL: '#fef9c3' };
            const TREND_C  = { BULLISH: '#16a34a', BEARISH: '#dc2626', NEUTRAL: '#b45309' };
            const fmtP = v => v != null ? '₹' + Number(v).toLocaleString('en-IN') : '—';

            if (isMobile) return (
              <div>
                {filteredResults.map(r => (
                  <div key={r.symbol} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: '0.5rem', padding: '0.6rem 0.75rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <span style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a' }}>{r.symbol}</span>
                        <span style={{ fontSize: '0.65rem', fontWeight: 700, marginLeft: 6, padding: '0.08rem 0.3rem', borderRadius: 3,
                          background: TREND_BG[r.signals?.trend] || '#f1f5f9', color: TREND_C[r.signals?.trend] || '#475569' }}>
                          {r.signals?.trend || '—'}
                        </span>
                      </div>
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#0f172a' }}>LTP {fmtP(r.ltp)}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.3rem', fontSize: '0.7rem' }}>
                      <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '0.08rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>Entry {fmtP(r.entry_price)}</span>
                      <span style={{ background: '#fee2e2', color: '#991b1b', padding: '0.08rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>SL {fmtP(r.stop_loss)}</span>
                      <span style={{ background: '#dcfce7', color: '#15803d', padding: '0.08rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>T1 {fmtP(r.target_1)}</span>
                      <span style={{ background: '#dcfce7', color: '#15803d', padding: '0.08rem 0.35rem', borderRadius: 3, fontWeight: 600 }}>T2 {fmtP(r.target_2)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.3rem' }}>
                      <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                        R:R 1:{r.risk_reward || '—'} · RSI {r.indicators?.rsi != null ? Number(r.indicators.rsi).toFixed(1) : '—'} · Qty {r.suggested_qty}
                      </div>
                      <button onClick={() => onAnalyse?.(r.symbol)}
                        style={{ fontSize: '0.7rem', padding: '0.18rem 0.5rem', border: '1px solid #93c5fd', borderRadius: 4, color: '#0369a1', background: '#eff6ff', cursor: 'pointer', fontWeight: 600 }}>
                        Setup
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            );

            // Desktop table
            return (
              <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.75rem' }}>
                  <thead>
                    <tr style={{ background: '#eff6ff', borderBottom: '2px solid #93c5fd' }}>
                      {['Symbol', 'Trend', 'LTP', 'Entry', 'Stop Loss', 'Target 1', 'Target 2', 'R:R', 'RSI', 'Qty', ''].map(h => (
                        <th key={h} style={{ padding: '0.35rem 0.45rem', textAlign: h === 'Symbol' || h === 'Trend' ? 'left' : 'right',
                          fontSize: '0.65rem', fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredResults.map(r => (
                      <tr key={r.symbol} style={{ borderBottom: '1px solid #f1f5f9' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}>
                        <td style={{ padding: '0.35rem 0.45rem', fontWeight: 700, color: '#0f172a' }}>{r.symbol}</td>
                        <td style={{ padding: '0.35rem 0.45rem' }}>
                          <span style={{ fontSize: '0.65rem', fontWeight: 700, color: TREND_C[r.signals?.trend] || '#475569',
                            background: TREND_BG[r.signals?.trend] || '#f1f5f9', padding: '0.08rem 0.3rem', borderRadius: 3 }}>{r.signals?.trend || '—'}</span>
                        </td>
                        <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right' }}>{fmtP(r.ltp)}</td>
                        <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right', color: '#0369a1', fontWeight: 600 }}>{fmtP(r.entry_price)}</td>
                        <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right', color: '#dc2626' }}>{fmtP(r.stop_loss)}</td>
                        <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right', color: '#16a34a' }}>{fmtP(r.target_1)}</td>
                        <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right', color: '#16a34a' }}>{fmtP(r.target_2)}</td>
                        <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right', fontWeight: 600 }}>1:{r.risk_reward || '—'}</td>
                        <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right' }}>{r.indicators?.rsi != null ? Number(r.indicators.rsi).toFixed(1) : '—'}</td>
                        <td style={{ padding: '0.35rem 0.45rem', textAlign: 'right' }}>{r.suggested_qty}</td>
                        <td style={{ padding: '0.35rem 0.45rem', textAlign: 'center' }}>
                          <button onClick={() => onAnalyse?.(r.symbol)}
                            style={{ fontSize: '0.62rem', padding: '0.08rem 0.3rem', border: '1px solid #93c5fd', borderRadius: 3, color: '#0369a1', background: '#eff6ff', cursor: 'pointer' }}>
                            Setup
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      )}

      {/* Mobile card view */}
      {stocks && isMobile && (
        <div>
          {sorted.slice(0, 200).map(s => (
            <div key={s.symbol} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: '0.5rem', padding: '0.6rem 0.75rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
                    <input type="checkbox" checked={selected.has(s.symbol)} onChange={() => toggleSelect(s.symbol)} style={{ cursor: 'pointer' }} />
                    <span style={{ fontWeight: 800, fontSize: '0.88rem', color: '#0f172a' }}>{s.symbol}</span>
                    <span style={{ fontSize: '0.65rem', color: '#64748b' }}>{s.sector || ''}</span>
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontWeight: 800, fontSize: '0.95rem', color: scoreColor(s.fund_score) }}>{s.fund_score}/9</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.3rem', fontSize: '0.7rem', color: '#64748b' }}>
                {s.pe_ratio != null && <span>PE {fmt(s.pe_ratio)}</span>}
                {s.roe != null && <span style={{ color: s.roe > 15 ? '#16a34a' : undefined }}>ROE {fmt(s.roe)}%</span>}
                {s.roce != null && <span style={{ color: s.roce > 15 ? '#16a34a' : undefined }}>ROCE {fmt(s.roce)}%</span>}
                {s.debt_equity != null && <span style={{ color: s.debt_equity < 0.5 ? '#16a34a' : s.debt_equity > 1 ? '#dc2626' : undefined }}>D/E {fmt(s.debt_equity)}</span>}
                {s.profit_growth != null && <span style={{ color: s.profit_growth > 10 ? '#16a34a' : undefined }}>PG {fmt(s.profit_growth)}%</span>}
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.35rem' }}>
                <button onClick={() => onAnalyse?.(s.symbol)}
                  style={{ fontSize: '0.68rem', padding: '0.15rem 0.45rem', border: '1px solid #93c5fd', borderRadius: 4, color: '#0369a1', background: '#eff6ff', cursor: 'pointer' }}>
                  Analyse
                </button>
                <button onClick={() => onEditFundamentals(s)}
                  style={{ fontSize: '0.68rem', padding: '0.15rem 0.4rem', border: '1px solid #c4b5fd', borderRadius: 4, color: '#7c3aed', background: '#f5f3ff', cursor: 'pointer' }}>
                  Edit
                </button>
              </div>
            </div>
          ))}
          {sorted.length === 0 && <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8' }}>No stocks. Use "Fetch All" to populate.</div>}
        </div>
      )}

      {/* Desktop table view */}
      {stocks && !isMobile && (() => {
        const cols = [
          { key: '_select',          label: '',           align: 'center', width: 35 },
          { key: 'fund_score',       label: 'Score',     align: 'center', width: 60 },
          { key: 'symbol',           label: 'Symbol',    align: 'left',   width: 100 },
          { key: 'sector',           label: 'Sector',    align: 'left',   width: 130 },
          { key: 'pe_ratio',         label: 'P/E',       align: 'right',  width: 65 },
          { key: 'roe',              label: 'ROE',       align: 'right',  width: 65 },
          { key: 'roce',             label: 'ROCE',      align: 'right',  width: 65 },
          { key: 'debt_equity',      label: 'D/E',       align: 'right',  width: 55 },
          { key: 'profit_growth',    label: 'Profit G',  align: 'right',  width: 75 },
          { key: 'revenue_growth',   label: 'Rev G',     align: 'right',  width: 70 },
          { key: 'promoter_holding', label: 'Promoter',  align: 'right',  width: 75 },
          { key: 'piotroski_score',  label: 'Piotroski', align: 'right',  width: 70 },
          { key: 'industry_pe',      label: 'Ind. PE',   align: 'right',  width: 65 },
          { key: 'operating_margin', label: 'OPM',       align: 'right',  width: 60 },
          { key: 'price_to_book',    label: 'P/B',       align: 'right',  width: 55 },
          { key: '_edit',            label: '',           align: 'center', width: 45 },
        ];
        const thStyle = {
          padding: '0.4rem 0.45rem', fontSize: '0.65rem', fontWeight: 700,
          color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.03em',
          whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
          background: '#f8fafc', borderBottom: '2px solid #e2e8f0',
        };
        const tdS = { padding: '0.32rem 0.45rem', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' };
        const scoreColor = sc => sc >= 7 ? '#16a34a' : sc >= 5 ? '#0369a1' : sc >= 3 ? '#b45309' : '#dc2626';

        return (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.76rem' }}>
              <thead>
                <tr>
                  {cols.map(c => (
                    <th key={c.key}
                      onClick={() => c.key === '_select' ? selectAllFiltered() : c.key !== '_edit' && toggleSort(c.key)}
                      style={{ ...thStyle, textAlign: c.align, width: c.width, cursor: 'pointer' }}>
                      {c.key === '_select' ? (
                        <input type="checkbox" checked={sorted.slice(0, 200).length > 0 && sorted.slice(0, 200).every(s => selected.has(s.symbol))}
                          onChange={selectAllFiltered} style={{ cursor: 'pointer' }} />
                      ) : (
                        <>{c.label}{sortField === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}</>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.slice(0, 200).map(s => (
                  <tr key={s.symbol} style={{ borderBottom: '1px solid #f1f5f9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    <td style={{ ...tdS, textAlign: 'center' }}>
                      <input type="checkbox" checked={selected.has(s.symbol)} onChange={() => toggleSelect(s.symbol)} style={{ cursor: 'pointer' }} />
                    </td>
                    <td style={{ ...tdS, textAlign: 'center', fontWeight: 800, fontSize: '0.85rem', color: scoreColor(s.fund_score) }}>
                      {s.fund_score}/9
                    </td>
                    <td style={{ ...tdS, fontWeight: 700, color: '#0f172a' }}>{s.symbol}</td>
                    <td style={{ ...tdS, color: '#64748b', fontSize: '0.7rem' }}>{s.sector || '—'}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{fmt(s.pe_ratio)}</td>
                    <td style={{ ...tdS, textAlign: 'right', color: s.roe > 15 ? '#16a34a' : '#0f172a' }}>{fmt(s.roe)}%</td>
                    <td style={{ ...tdS, textAlign: 'right', color: s.roce > 15 ? '#16a34a' : '#0f172a' }}>{fmt(s.roce)}%</td>
                    <td style={{ ...tdS, textAlign: 'right', color: s.debt_equity != null && s.debt_equity < 0.5 ? '#16a34a' : s.debt_equity > 1 ? '#dc2626' : '#0f172a' }}>{fmt(s.debt_equity)}</td>
                    <td style={{ ...tdS, textAlign: 'right', color: s.profit_growth > 10 ? '#16a34a' : '#0f172a' }}>{fmt(s.profit_growth)}%</td>
                    <td style={{ ...tdS, textAlign: 'right', color: s.revenue_growth > 10 ? '#16a34a' : '#0f172a' }}>{fmt(s.revenue_growth)}%</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{fmt(s.promoter_holding)}%</td>
                    <td style={{ ...tdS, textAlign: 'right', color: s.piotroski_score >= 7 ? '#16a34a' : s.piotroski_score != null ? '#0f172a' : '#94a3b8' }}>{fmt(s.piotroski_score, 0)}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{fmt(s.industry_pe)}</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{fmt(s.operating_margin)}%</td>
                    <td style={{ ...tdS, textAlign: 'right' }}>{fmt(s.price_to_book)}</td>
                    <td style={{ ...tdS, textAlign: 'center', whiteSpace: 'nowrap' }}>
                      <button onClick={() => onAnalyse?.(s.symbol)}
                        style={{ fontSize: '0.63rem', padding: '0.08rem 0.35rem', border: '1px solid #93c5fd', borderRadius: 3, color: '#0369a1', background: '#eff6ff', cursor: 'pointer', marginRight: 3 }}>
                        Analyse
                      </button>
                      <button onClick={() => onEditFundamentals(s)}
                        style={{ fontSize: '0.63rem', padding: '0.08rem 0.3rem', border: '1px solid #c4b5fd', borderRadius: 3, color: '#7c3aed', background: '#f5f3ff', cursor: 'pointer' }}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sorted.length === 0 && (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
                No other stocks with fundamentals data. Use "Fetch All" or enter symbols to populate.
              </div>
            )}
            {sorted.length > 200 && (
              <div style={{ padding: '0.5rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.75rem' }}>
                Showing first 200 of {sorted.length}. Use the filter to narrow down.
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function TechAnalysisTab() {
  const [data,        setData]       = useState(null);
  const [status,      setStatus]     = useState('loading'); // loading | done | error | refreshing
  const [expandedSym, setExpanded]   = useState(null);
  const [updatedAt,   setUpdatedAt]  = useState(null);
  const [errorMsg,    setErrorMsg]   = useState(null);
  const [editingRow,  setEditingRow] = useState(null);
  const [swingSymbol, setSwingSymbol] = useState(null);
  const [isMobile, setIsMobile]      = useState(window.innerWidth <= 768);
  const [bulkSymbols, setBulkSymbols] = useState('');
  const [bulkStatus, setBulkStatus]   = useState(null);

  React.useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const runBulkAnalysis = () => {
    const syms = bulkSymbols.split(',').map(s => s.trim().toUpperCase()).filter(Boolean);
    if (!syms.length) return;
    setBulkStatus('Running…');
    authFetch(`/api/portfolio/fundamentals/fetch?scope=${encodeURIComponent(syms.join(','))}`, { method: 'POST' })
      .then(r => r.json())
      .then(() => setBulkStatus(`Fetching fundamentals for ${syms.length} stocks. Reload in a few minutes.`))
      .catch(e => setBulkStatus(`Error: ${e.message}`));
  };

  const load = (refresh = false) => {
    setStatus(refresh ? 'refreshing' : 'loading');
    setErrorMsg(null);
    const url = refresh ? '/api/portfolio/analysis' : '/api/portfolio/analysis/cached';
    authFetch(url)
      .then(async r => {
        const txt = await r.text();
        let parsed = null;
        try { parsed = JSON.parse(txt); } catch { /* non-JSON body */ }
        if (!r.ok || !parsed) {
          const msg = parsed?.detail || (txt.slice(0, 300)) || `HTTP ${r.status}`;
          if (!refresh) { setData([]); setStatus('done'); }
          else { setErrorMsg(msg); setStatus('error'); }
          return;
        }
        setData(parsed.results || []);
        if (parsed.results?.length) setUpdatedAt(parsed.results[0].updated_at);
        setStatus('done');
      })
      .catch(e => {
        if (!refresh) { setData([]); setStatus('done'); }
        else { setErrorMsg(e.message || 'Network error'); setStatus('error'); }
      });
  };

  useEffect(() => { load(false); }, []);

  // Track which section is in view for floating nav
  const [visibleSection, setVisibleSection] = useState('holdings');
  React.useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) setVisibleSection(e.target.id === 'other-stocks-section' ? 'other' : 'holdings');
      });
    }, { threshold: 0.1 });
    const h = document.getElementById('holdings-section');
    const o = document.getElementById('other-stocks-section');
    if (h) observer.observe(h);
    if (o) observer.observe(o);
    return () => observer.disconnect();
  }, [data]);

  const rows = data || [];
  const recOrder = { 'ADD MORE': 0, HOLD: 1, TRIM: 2, EXIT: 3 };
  const sorted = [...rows].sort((a, b) => (recOrder[a.recommendation] ?? 9) - (recOrder[b.recommendation] ?? 9)
    || (b.total_score ?? 0) - (a.total_score ?? 0));

  if (status === 'loading') return <div style={{ color: '#94a3b8', padding: '2rem' }}>Loading technical analysis…</div>;

  return (
    <div>
      {/* Floating nav button */}
      <div style={{ position: 'fixed', bottom: isMobile ? 70 : 24, right: 20, zIndex: 50 }}>
        <button
          onClick={() => document.getElementById(visibleSection === 'holdings' ? 'other-stocks-section' : 'holdings-section')?.scrollIntoView({ behavior: 'smooth' })}
          style={{ padding: '0.5rem 0.9rem', background: '#0369a1', color: '#fff', border: 'none', borderRadius: 20,
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700 }}>
          {visibleSection === 'holdings' ? '↓ Other Stocks' : '↑ Holdings'}
        </button>
      </div>

      {/* Header row */}
      <div id="holdings-section"></div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => load(true)}
          disabled={status === 'refreshing'}
          style={{ padding: '0.38rem 1rem', background: status === 'refreshing' ? '#bae6fd' : '#0ea5e9',
            color: '#fff', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: '0.82rem',
            cursor: status === 'refreshing' ? 'not-allowed' : 'pointer' }}
        >
          {status === 'refreshing' ? 'Running…' : '↺ Refresh Now'}
        </button>
        {updatedAt && (
          <span style={{ fontSize: '0.73rem', color: '#94a3b8' }}>
            Last run: {new Date(updatedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
            {' '}· Auto-refreshes daily at 4:05 PM IST
          </span>
        )}
        {!updatedAt && rows.length === 0 && (
          <span style={{ fontSize: '0.73rem', color: '#f59e0b' }}>
            No data yet — click Refresh Now to run the first analysis.
          </span>
        )}
      </div>

      {/* Error banner — shown inline so Refresh button above stays clickable */}
      {status === 'error' && (
        <div style={{ background: '#1e0a0a', border: '1px solid #fca5a5', borderRadius: 6,
          padding: '0.65rem 1rem', marginBottom: '0.75rem', fontSize: '0.8rem', color: '#fca5a5' }}>
          ⚠ Analysis failed — {errorMsg || 'Unknown error'}
        </div>
      )}

      {/* Summary pills */}
      {rows.length > 0 && (() => {
        const counts = rows.reduce((acc, r) => { acc[r.recommendation] = (acc[r.recommendation] || 0) + 1; return acc; }, {});
        return (
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            {Object.entries(counts).map(([rec, n]) => (
              <span key={rec} style={{ ...REC_STYLE[rec] && { background: REC_STYLE[rec].bg, color: REC_STYLE[rec].color, border: `1px solid ${REC_STYLE[rec].border}` },
                borderRadius: 6, padding: '0.18rem 0.6rem', fontSize: '0.75rem', fontWeight: 700 }}>
                {rec}: {n}
              </span>
            ))}
          </div>
        );
      })()}

      {/* Bulk Analysis */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          placeholder="INFY,TCS,RELIANCE — bulk analyse"
          value={bulkSymbols}
          onChange={e => setBulkSymbols(e.target.value.toUpperCase())}
          style={{ padding: '0.32rem 0.6rem', border: '1px solid #cbd5e1', borderRadius: 5, fontSize: '0.78rem', width: isMobile ? '100%' : 220 }}
        />
        <button onClick={runBulkAnalysis} disabled={!bulkSymbols.trim()}
          style={{ padding: '0.32rem 0.7rem', background: bulkSymbols.trim() ? '#7c3aed' : '#e2e8f0', color: bulkSymbols.trim() ? '#fff' : '#94a3b8',
            border: 'none', borderRadius: 5, fontSize: '0.78rem', fontWeight: 600, cursor: bulkSymbols.trim() ? 'pointer' : 'not-allowed' }}>
          Bulk Fetch
        </button>
        {bulkStatus && <span style={{ fontSize: '0.72rem', color: '#16a34a' }}>{bulkStatus}</span>}
      </div>

      {/* Holdings Analysis — mobile cards or desktop table */}
      {isMobile ? (
        <div>
          {sorted.map(row => (
            <TechCard
              key={row.symbol}
              row={row}
              expanded={expandedSym === row.symbol}
              onToggle={() => setExpanded(expandedSym === row.symbol ? null : row.symbol)}
              onEditFundamentals={r => setEditingRow(r)}
            />
          ))}
          {rows.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.85rem' }}>
              No analysis data yet. Click <strong style={{ color: '#38bdf8' }}>↺ Refresh Now</strong> above.
            </div>
          )}
        </div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #e2e8f0' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                {['Symbol', 'Signal', 'Score', 'RSI', 'EMA', 'MACD', 'P&L%', ''].map(h => (
                  <th key={h} style={{ padding: '0.5rem 0.6rem', textAlign: 'left', fontSize: '0.7rem',
                    fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map(row => (
                <TechRow
                  key={row.symbol}
                  row={row}
                  expanded={expandedSym === row.symbol}
                  onToggle={() => setExpanded(expandedSym === row.symbol ? null : row.symbol)}
                  onEditFundamentals={r => setEditingRow(r)}
                />
              ))}
            </tbody>
          </table>
          {rows.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8', fontSize: '0.88rem' }}>
              No analysis data yet. The scheduler runs daily at 4:05 PM IST after market close —
              or click <strong style={{ color: '#38bdf8' }}>↺ Refresh Now</strong> above to run it immediately.
            </div>
          )}
        </div>
      )}

      {/* Other stocks section */}
      <OtherStocksFundamentals
        onEditFundamentals={r => setEditingRow({ symbol: r.symbol, fundamentals: r })}
        onAnalyse={sym => setSwingSymbol(sym)}
        isMobile={isMobile}
      />

      {/* Fundamentals Editor modal */}
      {editingRow && (
        <FundamentalsEditor
          symbol={editingRow.symbol}
          existing={editingRow.fundamentals || {}}
          onClose={() => setEditingRow(null)}
          onSaved={() => { setEditingRow(null); load(true); }}
        />
      )}

      {/* Swing Setup modal */}
      {swingSymbol && (
        <SwingSetupModal symbol={swingSymbol} onClose={() => setSwingSymbol(null)} />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function PortfolioAnalysis() {
  const isMobile = window.innerWidth <= 768;
  const [tab, setTab] = useState('ai');

  const TAB_BTN = (id, label) => (
    <button
      key={id}
      onClick={() => setTab(id)}
      style={{
        padding: '0.4rem 1.1rem', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer',
        border: '1px solid #334155', borderRadius: 6,
        background: tab === id ? '#0c4a6e' : 'transparent',
        color:      tab === id ? '#38bdf8' : '#94a3b8',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{ padding: isMobile ? '0.75rem' : '1rem', maxWidth: 1100 }}>
      <h1 style={{ margin: '0 0 0.75rem', fontSize: isMobile ? '1.05rem' : '1.3rem', fontWeight: 800, color: '#e2e8f0' }}>
        Portfolio Analysis
      </h1>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {TAB_BTN('tech', 'Technical Analysis')}
        {TAB_BTN('ai',   'AI Analysis')}
      </div>
      {tab === 'tech' ? <TechAnalysisTab /> : <AiAnalysisTab />}
    </div>
  );
}

const th = {
  padding: '0.55rem 0.75rem',
  fontSize: '0.75rem',
  fontWeight: 700,
  color: '#64748b',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};
